import { CommonModule } from '@angular/common';
import { Component, HostListener } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CaseLinksComponent } from 'app/case-links/case-links.component';
import { CaseHistoryComponent } from 'app/shared/components/case-history/case-history.component';
import { CaseFormSectionContainerComponent } from 'app/shared/components/forms/case-form-section-container/case-form-section-container.component';
import { InterestedPartiesFormComponent } from 'app/shared/components/forms/interested-parties-form/interested-parties-form.component';
import { ProblemRecordProblemDetailsFormComponent } from 'app/shared/components/forms/problem-record-problem-details-form/problem-record-problem-details-form.component';
import { TabsWithContentComponent } from 'app/shared/components/tabs-with-content/tabs-with-content.component';
import { TitleHeaderComponent } from 'app/shared/components/title-header/title-header.component';
import { DynamicTableComponent } from 'app/shared/components/dynamic-table/dynamic-table.component';
import { ConfigurationService } from 'app/shared/services/configuration.service';
import { finalize, take } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { ToastTypesEnum } from 'app/shared/enums/toast.enum';
import { ProblemRecordsFormService } from './problem-records-form.service';
import { ProblemRecordProblemInfoFormComponent } from 'app/shared/components/forms/problem-record-problem-info-form/problem-record-problem-info-form.component';
import { ProblemRecordActivityFormComponent } from 'app/shared/components/forms/problem-record-activity-form/problem-record-activity-form.component';
import { ProblemRecordOwnerFormComponent } from 'app/shared/components/forms/problem-record-owner-form/problem-record-owner-form.component';
import { ProblemRecordsCaseRecordsComponent } from 'app/shared/components/problem-records-case-records/problem-records-case-records.component';
import { UsersService } from 'app/shared/services/users.service';
import { IUser } from 'app/shared/models/users.model';
import { HelperService } from 'app/shared/services/helper.service';
import { CanComponentDeactivate } from 'app/shared/guards/can-deactivate.guard';
import { DiscardChangesService } from 'app/shared/services/discard-changes.service';
import { HistoryEntityNamesEnum, HistoryTabsEnum } from 'app/shared/enums/history-entity.enum';
import { IHistoryTab } from 'app/shared/models/history.model';
import { ICredentials } from 'app/shared/models/credentials.model';
import { AuthService } from 'app/shared/services/auth.service';
import { BreadcrumbService } from 'app/shared/services/breadcrumb.service';
import { CaseCommentsComponent } from "app/shared/components/case-comments/case-comments.component";
import { CaseTypeEnum } from 'app/shared/enums/case-type.enum';


@Component({
  selector: 'app-problem-records-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TitleHeaderComponent,
    TabsWithContentComponent,
    TranslateModule,
    CaseFormSectionContainerComponent,
    ProblemRecordProblemDetailsFormComponent,
    InterestedPartiesFormComponent,
    CaseHistoryComponent,
    CaseLinksComponent,
    DynamicTableComponent,
    ProblemRecordProblemInfoFormComponent,
    ProblemRecordActivityFormComponent,
    ProblemRecordOwnerFormComponent,
    ProblemRecordsCaseRecordsComponent,
    CaseCommentsComponent
],
  templateUrl: './problem-records-form.component.html',
  styleUrl: './problem-records-form.component.scss'
})
export class ProblemRecordsFormComponent implements CanComponentDeactivate {
  tabs: string[] = ['summary', 'case_records', 'history', 'comments'];
  historyTabs: IHistoryTab[] = [];
  selectedTab: string = this.tabs[0];
  problemRecordForm!: FormGroup;
  linksForm!: FormGroup;
  viewMode: boolean = false;
  linksEditMode: boolean = false;
  productFamilyItems: any = [];
  problemRecordId!: number;
  problemPriorityItems: any = [];
  stageItems: any = [];
  problemRecordIdentifier: string = '';
  newCaseRecords: any = [];
  linksEditable: boolean = true;
  uploadedFiles: any[] = [];
  loading: boolean = false;
  attachmentsTouched!: boolean;
  credentials!: ICredentials | undefined;
  nextActionValidators!: boolean;
  backButtonLink!: string;
  problemRecordObject!: any;
  problemStatus!: string;
  caseType: string = CaseTypeEnum.PROBLEM_RECORD

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private configurationService: ConfigurationService,
    private translate: TranslateService,
    private toastr: ToastrService,
    private problemRecordsFormService: ProblemRecordsFormService,
    private activeRoute: ActivatedRoute,
    private usersService: UsersService,
    private helperService: HelperService,
    private discardChangesService: DiscardChangesService,
    private authService: AuthService,
    private breadcrumbService: BreadcrumbService
  ) { }

  ngOnInit(): void {
    this.createForm();
    this.getUsers();
    this.credentials = this.authService.getCredentials();
    this.problemPriorityItems = this.configurationService.getProblemRecordPriorityItems();
    this.stageItems = this.configurationService.getProblemRecordStageItems();

    this.activeRoute.params.subscribe((params: any) => {
      if (params['id']) {
        this.problemRecordId = Number(params['id']);
        this.backButtonLink = this.breadcrumbService.getPreviousUrl();
        this.getProblemRecord(this.problemRecordId);
        this.viewMode = true;

        window.scroll({
          top: 0,
          left: 0,
          behavior: 'smooth'
        });
      }
    });

    this.activeRoute.queryParams.subscribe((params) => {
      if (params['tab'] && this.tabs.some(t => t === params['tab'])) {
        this.onSelectedTab(params['tab']);
      } else {
        this.onSelectedTab(this.tabs[0]);
      }
    });
  }

  getProblemRecord(id: number, isReload?: boolean) {
    if (!isReload) {
      this.loading = true;
    }

    this.problemRecordsFormService.getProblemRecord(id)
      .pipe(
        take(1),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe((res: any) => {
        this.attachmentsTouched = false;
        this.historyTabs = [
          { name: HistoryTabsEnum.SUMMARY, value: HistoryEntityNamesEnum.PROBLEM_RECORD, entityId: this.problemRecordId, hidden: true }
        ];
        this.helperService.convertUsersToObjects(res, ['bookmarkOnly', 'allActivityUpdates', 'caseClosureNotifications'], true);
        res.ownerId = Number(res.ownerId);
        this.problemRecordIdentifier = res.problemRecordIdentifier;
        this.uploadedFiles = (res.attachments ?? []).map((file: any) => ({
          id: file.id,
          name: file.fileName,
          blobStorageId: file.storageFileId,
          type: file.mimeType,
        }));

        this.problemStatus = res.problemStatus;

        this.problemRecordObject = res;

        this.problemRecordForm.patchValue({
          ...res,
          attachments: this.uploadedFiles
        }, { emitEvent: false });

        this.setNextActionValidators(res.ownerId);

        this.updateLinksForm(res.links);
        this.setProductSuiteProductCompany(res);

        this.updateAttachments(this.uploadedFiles ?? []);

      })
  }

  getUsers() {
    this.usersService.getUsers('').subscribe((users: IUser[]) => {
      this.usersService.setUsers(users);
    });
  }

  setNextActionValidators(ownerId: number) {
    const userIsTicketOwner: boolean = ownerId === this.credentials?.id;
    this.problemRecordForm.controls['nextActionOwnerId'].setValidators(userIsTicketOwner ? [Validators.required] : []);
    this.problemRecordForm.controls['nextActionDueDate'].setValidators(userIsTicketOwner ? [Validators.required] : []);
    this.problemRecordForm.controls['nextActionDescription'].setValidators(userIsTicketOwner ? [Validators.required] : []);
    this.problemRecordForm.get('nextActionOwnerId')?.updateValueAndValidity();
    this.problemRecordForm.get('nextActionDueDate')?.updateValueAndValidity();
    this.problemRecordForm.get('nextActionDescription')?.updateValueAndValidity();
    this.nextActionValidators = userIsTicketOwner;
  }

  updateAttachments(attachemnts: any[]) {
    this.problemRecordForm.get('attachments')?.setValue([...attachemnts]);
    this.problemRecordForm.get('attachments')?.updateValueAndValidity();
  }


  updateLinksForm(linksData: any) {
    if (linksData) {
      let links: any = {};
      const typeCounter: any = {};

      linksData.forEach((element: any) => {
        element.isChanged = false;
        if (!typeCounter[element.type]) {
          typeCounter[element.type] = 0;
        }

        typeCounter[element.type] += 1;
        links[`${element.type}${typeCounter[element.type]}`] = element;
      });

      this.linksForm.get('links')?.patchValue(links, { emitEvents: false });
    }
  }

  setProductSuiteProductCompany(res: any) {
    this.problemRecordForm.get('productSuite')?.setValue(res.productSuite ? res.productSuite : null);
    this.problemRecordForm.get('productCompany')?.setValue(res.productCompany ? res.productCompany : null);
  }

  createForm() {
    this.problemRecordForm = this.fb.group({});
    this.problemRecordForm.addControl('id', new FormControl(null, []));
    this.problemRecordForm.addControl('attachments', new FormControl([]));
    this.linksForm = this.fb.group({});
  }

  submitForm(isEdit?: boolean) {
    if (!this.problemRecordForm.valid || !this.problemRecordForm.valid) {
      const errorMessage = this.translate.instant('messages.please_fill_in_all_required_fields');

      this.toastr.show(
        errorMessage,
        ToastTypesEnum.ERROR
      );

      Object.keys(this.problemRecordForm.controls).forEach(field => {
        const control = this.problemRecordForm.get(field);

        if (control && control.status === 'INVALID' && !!control.errors) {
          control.markAsTouched();
          control.updateValueAndValidity();
        }
      });

      return;
    }
    let problemRecordFormValue = this.problemRecordForm.getRawValue();

    if (this.nextActionValidators && this.helperService.isDateInThePast(problemRecordFormValue.nextActionDueDate)) {
      this.toastr.show(this.translate.instant('messages.next_action_due_date_cannot_be_in_the_past'), ToastTypesEnum.ERROR);
      return
    }

    this.helperService.convertUsersToStrings(problemRecordFormValue, ['bookmarkOnly', 'allActivityUpdates', 'caseClosureNotifications'], true);

    problemRecordFormValue.caseIds = [];

    if (this.newCaseRecords) {
      let newCaseRecordIds: any = [];

      this.newCaseRecords.forEach((element: any) => {
        newCaseRecordIds.push(element.id)
      });

      problemRecordFormValue.caseIds = newCaseRecordIds;
    }

    problemRecordFormValue.attachments = (this.problemRecordForm.get('attachments')?.value || []).map((attachment: any) => ({
      id: attachment.id,
      fileName: attachment.name,
      storageFileId: attachment.blobStorageId,
      mimeType: attachment.type
    }));

    this.problemRecordsFormService.saveProblemRecords(problemRecordFormValue)
      .pipe(take(1))
      .subscribe((res: any) => {
        if (!isEdit) {
          this.problemRecordForm.reset();
          this.router.navigate(['problem-record/' + res]);
        } else {
          this.reloadProblemRecord();
        }
      });

  }

  reloadProblemRecord() {
    this.getProblemRecord(this.problemRecordId, true);
    this.problemRecordForm.markAsPristine();
    this.linksForm.markAsPristine();
    this.viewMode = true;
  }


  submitLinks($event: boolean) {
    this.linksEditMode = $event;
    if (!this.linksEditMode) {
      const links = this.linksForm.get('links')?.value;
      let linksArr: any[] = [];
      Object.keys(links).forEach(key => {
        const linkItem = links[key];

        if (linkItem.isChanged) {
          linkItem.isActive = !!(linkItem.title && linkItem.url);
          if (linkItem.title && linkItem.url || (!linkItem.title && !linkItem.url)) {
            linksArr.push(linkItem);
          }
        }
      });

      const linksFormValue = linksArr;
      if (!linksFormValue.length) {
        return;
      }
      this.problemRecordsFormService.saveProblemRecordLinks(this.problemRecordId, linksFormValue)
        .pipe(take(1))
        .subscribe(() => {
          this.toastr.show(this.translate.instant('messages.links_successfully_saved'), ToastTypesEnum.SUCCESS);
          this.linksForm.markAsPristine();
        })
    }
  }

  getNewCaseRecords(data: any) {
    this.newCaseRecords = data;
  }

  onSelectedTab(tabName: string): void {
    this.selectedTab = tabName;
  }

  onLeaveProblemRecord(tabName?: string) {

    this.discardChangesService.setUnsavedChangesState(this.problemRecordForm.dirty || this.linksForm.dirty);

    if (!this.problemRecordId && !this.problemRecordForm.dirty) {
      this.router.navigate(['/problem-records']);
      return;
    }

    this.discardChangesService.canDeactivate().then(
      (result) => {
        if (result) {

          if (tabName) {
            this.selectTab(tabName);
          }

          if (this.problemRecordId) {
            this.reloadProblemRecord();
          } else {
            this.problemRecordForm.reset();
            this.linksForm.reset();
            this.problemRecordForm.markAsPristine();
            this.linksForm.markAsPristine();
            this.router.navigate(['/problem-records']);
          }
        }
      }
    );
  }

  selectTab(tabName: string) {
    this.selectedTab = tabName;
    this.router.navigate([], {
      relativeTo: this.activeRoute,
      queryParams: { tab: tabName }
    });
  }

  isUnsavedChanges(): boolean {
    this.discardChangesService.setUnsavedChangesState(this.problemRecordForm.dirty || this.linksForm.dirty);

    return this.discardChangesService.getUnsavedChangesState();
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification(event: BeforeUnloadEvent): void {
    this.discardChangesService.setUnsavedChangesState(this.problemRecordForm.dirty || this.linksForm.dirty);

    const hasChanges = this.discardChangesService.getUnsavedChangesState();
    if (hasChanges) {
      event.preventDefault();
      event.returnValue = this.translate.instant('messages.unsaved_changes_message');
    }
  }

}
